import { getToken } from "@/app/infrastructures/misc/Cookies";
import { UnleashClient } from "unleash-proxy-client";
import { reactive } from "vue";

// Define state keys as object selector
const StateKey = {
  FCS_CONTEXT: "fcscontext",
  BCS_CONTEXT: "bcscontext",
  FCS_TOGGLES: "fcstoggles",
  BCS_TOGGLES: "bcstoggles",
} as const;

// Environment configuration constants
const ENV_CONFIG = {
  FCS: {
    URL: process.env.VUE_APP_FCS_OPENFEATURE_URL || "",
    CLIENT_KEY: process.env.VUE_APP_FCS_OPENFEATURE_CLIENT_KEY || "",
    APP_NAME: process.env.VUE_APP_FCS_OPENFEATURE_APP_NAME || "default",
  },
  BCS: {
    URL: process.env.VUE_APP_BCS_OPENFEATURE_URL || "",
    CLIENT_KEY: process.env.VUE_APP_BCS_OPENFEATURE_CLIENT_KEY || "",
    APP_NAME: process.env.VUE_APP_BCS_OPENFEATURE_APP_NAME || "default",
  },
} as const;

// String constants to avoid primitive obsession
const STRING_CONSTANTS = {
  UPDATE_EVENT: "update",
  EMPTY_STRING: "",
  NAMESPACE_SEPARATOR: ".",
  VUE_PLUGIN_PROPERTIES: {
    UNLEASH_STATE: "$unleashState",
    FCS_FLAG_CLIENT: "$fcsFlagClient",
    BCS_FLAG_CLIENT: "$bcsFlagClient",
    FCS_FLAG: "$fcsFlag",
    BCS_FLAG: "$bcsFlag",
  },
} as const;

// Simple flag name type
type FlagName = string;

// Define proper types to avoid primitive obsession
interface UnleashState {
  [key: string]: string;
  fcscontext: string;
  bcscontext: string;
  fcstoggles: string;
  bcstoggles: string;
}

interface FlagContext {
  [key: string]: unknown;
}

interface FlagObject {
  name: string;
  setName: (name: FlagName) => void;
}

// Client configuration type
interface ClientConfig {
  name: string;
  url: string;
  clientKey: string;
  appName: string;
  contextKey: typeof StateKey[keyof typeof StateKey];
  togglesKey: typeof StateKey[keyof typeof StateKey];
}

// Create a reactive state store to trigger rerenders
export const unleashState = reactive<UnleashState>({
  [StateKey.FCS_CONTEXT]: "",
  [StateKey.BCS_CONTEXT]: "",
  [StateKey.FCS_TOGGLES]: "",
  [StateKey.BCS_TOGGLES]: "",
});

// Client configurations
const clientConfigs: ClientConfig[] = [
  {
    name: "fcs",
    url: ENV_CONFIG.FCS.URL,
    clientKey: ENV_CONFIG.FCS.CLIENT_KEY,
    appName: ENV_CONFIG.FCS.APP_NAME,
    contextKey: StateKey.FCS_CONTEXT,
    togglesKey: StateKey.FCS_TOGGLES,
  },
  {
    name: "bcs",
    url: ENV_CONFIG.BCS.URL,
    clientKey: ENV_CONFIG.BCS.CLIENT_KEY,
    appName: ENV_CONFIG.BCS.APP_NAME,
    contextKey: StateKey.BCS_CONTEXT,
    togglesKey: StateKey.BCS_TOGGLES,
  },
];

// Create clients dynamically
const clients = new Map<string, UnleashClient>();
clientConfigs.forEach((config) => {
  try {
    clients.set(
      config.name,
      new UnleashClient({
        url: config.url,
        clientKey: config.clientKey,
        appName: config.appName,
      })
    );
  } catch (error) {
    console.error(`Failed to create Unleash client ${config.name}:`, error);
    return;
  }
});

// Export clients for backward compatibility
export const fcsFlagClient = clients.get("fcs")!;
export const bcsFlagClient = clients.get("bcs")!;

const triggerReactive = (client: UnleashClient, config: ClientConfig) => {
  const originalIsEnabled = client.isEnabled;
  const originalGetVariant = client.getVariant;
  // Override methods to make them reactive
  client.isEnabled = function(name: string): boolean {
    // Access the reactive properties to create dependencies
    const _context = unleashState[config.contextKey];
    const _toggles = unleashState[config.togglesKey];

    const result = originalIsEnabled.call(this, name);

    return result;
  };

  client.getVariant = function(name: string) {
    // Access the reactive properties to create dependencies
    const _context = unleashState[config.contextKey];
    const _toggles = unleashState[config.togglesKey];

    const result = originalGetVariant.call(this, name);

    return result;
  };
}

// Helper function to make client methods reactive
function makeClientReactive(client: UnleashClient, config: ClientConfig) {

  // Set up listener for updates
  client.on(STRING_CONSTANTS.UPDATE_EVENT, () => {
    const context = client.getContext() as any;
    unleashState[config.contextKey] = JSON.stringify(client.getContext());
    unleashState[config.togglesKey] = JSON.stringify(client.getAllToggles());
  });

  triggerReactive(client, config);
}

let isReady = false;

export const waitForUnleashReadiness = (isProfileError = false): Promise<void> => {
  return new Promise((resolve) => {
      const token = getToken()
      
      if (!token || isReady || isProfileError) {
        resolve();
        return;
      }
      Promise.allSettled(clientConfigs.map((config) => {
        const client = clients.get(config.name);
        if (!client) {
          return Promise.resolve();
        }
        return new Promise<void>((res) => {
          let timeoutInstance: any = null;
          const checkContext = async () => {
            const context = client.getContext() as any;
            if (context?.email) {
              isReady = true;
              client.off(STRING_CONSTANTS.UPDATE_EVENT, checkContext);
              clearTimeout(timeoutInstance);
              await client.updateToggles();
              res();
            }
          }
          client.on(STRING_CONSTANTS.UPDATE_EVENT, checkContext);
          timeoutInstance = setTimeout(() => {
            console.log(`Unleash client ${config.name} readiness check timed out.`);
            client.off(STRING_CONSTANTS.UPDATE_EVENT, checkContext);
            res();
          }, 10000);
        });
      })).then(() => {
        resolve();
      });
  });
};

// Initialize both clients
export const initUnleashClients = async () => {
  try {
    await Promise.allSettled(
      clientConfigs.map((config) => {
        const client = clients.get(config.name);
        if (!client) {
          console.error(`[Unleash Debug] Client ${config.name} not found!`);
          return Promise.resolve();
        }
        makeClientReactive(client, config);
        return client.start();
      })
    );
  } catch (error) {
    console.error("Failed to initialize Unleash clients:", error);
    return;
  }
};

// Custom context object for targeting rules
export const updateUnleashContext = (context: FlagContext): void => {
  try {
    clients.forEach((client) => {
      client.updateContext(context);
    });
  } catch (error) {
    console.error("Failed to update Unleash context:", error);
    return;
  }
};

export const updateContextField = (field: string, value: string): void => {
  try {
    clients.forEach((client) => {
      client.setContextField(field, value);
    });
  } catch (error) {
    console.error("Failed to update Unleash context:", error);
    return;
  }
};

// Base flag classes with abstraction
abstract class BaseFlag<T = unknown> {
  readonly defaultValue: T;
  protected _name: FlagName;
  protected client: UnleashClient;

  constructor(defaultValue: T, client: UnleashClient, flagName?: FlagName) {
    this.defaultValue = defaultValue;
    this.client = client;
    this._name = flagName || "";
  }

  get name(): string {
    return this._name;
  }

  setName(name: FlagName): void {
    if (!this._name) {
      this._name = name;
    }
  }

  unfreeze(): void {
    // No-op for compatibility with Rox
  }
}

class Flag extends BaseFlag<boolean> {
  constructor(
    defaultValue = false,
    client: UnleashClient,
    flagName?: FlagName
  ) {
    super(defaultValue, client, flagName);
  }

  isEnabled(): boolean {
    try {
      const variant = this.client.getVariant(this._name);

      // Check payload value for explicit boolean strings
      const payloadValue = variant.payload?.value;
      const parsedValue = Number(payloadValue);

      if (payloadValue && !isNaN(parsedValue)) {
        return !!parsedValue;
      }

      // If variant exists but no explicit payload, use default value
      return this.defaultValue;
    } catch (error) {
      console.error("Failed to get variant:", error);
      return this.defaultValue;
    }
  }
}

class StringFlag extends BaseFlag<string> {
  constructor(defaultValue = "", client: UnleashClient, flagName?: FlagName) {
    super(defaultValue, client, flagName);
  }

  getValue(): string {
    try {
      const variant = this.client.getVariant(this._name);
      return variant.payload?.value ?? this.defaultValue;
    } catch (error) {
      console.error("Failed to get variant:", error);
      return this.defaultValue;
    }
  }
}

class JSONFlag extends BaseFlag<Record<string, any>> {
  constructor(defaultValue = {} as Record<string, any>, client: UnleashClient, flagName?: FlagName) {
    super(defaultValue, client, flagName);
  }

  getValue(): Record<string, any> {
    try {
      const variant = this.client.getVariant(this._name);
      return JSON.parse(variant.payload?.value || JSON.stringify(this.defaultValue));
    } catch (error) {
      console.error("Failed to get variant:", error);
      return this.defaultValue;
    }
  }
}

class NumberFlag extends BaseFlag<number> {
  constructor(defaultValue = 0, client: UnleashClient, flagName?: FlagName) {
    super(defaultValue, client, flagName);
  }

  getValue(): number {
    try {
      const variant = this.client.getVariant(this._name);
      const variantValue = variant.payload?.value;
      if (variantValue === undefined) return this.defaultValue;
      const numValue = Number(variantValue);
      return isNaN(numValue) ? this.defaultValue : numValue;
    } catch (error) {
      console.error("Failed to get variant:", error);
      return this.defaultValue;
    }
  }
}

// Factory function to create flag namespaces
function createFlagNamespace(client: UnleashClient) {
  const registeredFlags: Record<string, Record<string, unknown>> = {};

  const isValidFlag = (flag: unknown): flag is FlagObject => {
    return (
      flag !== null &&
      flag !== undefined &&
      typeof flag === "object" &&
      "setName" in (flag as Record<string, unknown>)
    );
  };

  const shouldSetName = (
    flag: FlagObject,
    _namespace: string,
    _key: string
  ): boolean => {
    return (
      flag.name === STRING_CONSTANTS.EMPTY_STRING &&
      typeof flag.setName === "function"
    );
  };

  const buildFlagName = (namespace: string, key: string): FlagName => {
    return namespace
      ? `${namespace}${STRING_CONSTANTS.NAMESPACE_SEPARATOR}${key}`
      : key;
  };

  return {
    register(namespace: string, flagContainer: Record<string, unknown>): void {
      registeredFlags[namespace] = flagContainer;

      // Iterate through flags to set their names based on the keys
      for (const key in flagContainer) {
        const flag = flagContainer[key];

        if (isValidFlag(flag) && shouldSetName(flag, namespace, key)) {
          const flagName = buildFlagName(namespace, key);
          flag.setName(flagName);
        }
      }
    },

    Flag: class extends Flag {
      constructor(defaultValue = false, flagName?: FlagName) {
        super(defaultValue, client, flagName);
      }
    },

    StringFlag: class extends StringFlag {
      constructor(defaultValue = "", flagName?: FlagName) {
        super(defaultValue, client, flagName);
      }
    },

    NumberFlag: class extends NumberFlag {
      constructor(defaultValue = 0, flagName?: FlagName) {
        super(defaultValue, client, flagName);
      }
    },

    JSONFlag: class extends JSONFlag {
      constructor(defaultValue = {} as Record<string, any>, flagName?: FlagName) {
        super(defaultValue, client, flagName);
      }
    },

    isEnabled(flagName: string, defaultValue = false): boolean {
      const result = client.isEnabled(flagName) ?? defaultValue;
      return result;
    },

    getVariant(flagName: string, defaultValue = ""): string {
      const result = client.getVariant(flagName)?.name ?? defaultValue;
      return result;
    },
  };
}

// Create flag namespaces using the factory
export const FcsFlag = createFlagNamespace(fcsFlagClient);
export const BcsFlag = createFlagNamespace(bcsFlagClient);

export const createUnleashPlugin = () => {
  return {
    install(Vue: any) {
      // Make the reactive state accessible
      Vue.config.globalProperties[
        STRING_CONSTANTS.VUE_PLUGIN_PROPERTIES.UNLEASH_STATE
      ] = unleashState;

      // Provide client access
      Vue.config.globalProperties[
        STRING_CONSTANTS.VUE_PLUGIN_PROPERTIES.FCS_FLAG_CLIENT
      ] = fcsFlagClient;
      Vue.config.globalProperties[
        STRING_CONSTANTS.VUE_PLUGIN_PROPERTIES.BCS_FLAG_CLIENT
      ] = bcsFlagClient;

      // Provide flag namespace access
      Vue.config.globalProperties[
        STRING_CONSTANTS.VUE_PLUGIN_PROPERTIES.FCS_FLAG
      ] = FcsFlag;
      Vue.config.globalProperties[
        STRING_CONSTANTS.VUE_PLUGIN_PROPERTIES.BCS_FLAG
      ] = BcsFlag;
    },
  };
};
